import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import { generateLocalBusinessSchema } from '../utils/schemaGenerator';
import useCMSContent from '../hooks/useCMSContent';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const HomePage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const cms = useCMSContent('homepage');

  useEffect(() => {
    axios.get(`${API_URL}/api/testimonials`)
      .then(res => setTestimonials(res.data.testimonials || []))
      .catch(() => {});
  }, []);

  const services = [
    {
      title: 'Kitchen Remodels',
      desc: 'Custom cabinetry, countertops, and modern kitchen designs for Connecticut homes',
      image: 'kitchen-remodel-marble-countertop-custom-cabinets-connecticut.jpg',
      alt: 'Kitchen remodeling contractor Connecticut - custom marble countertops wood cabinets by Visser licensed insured',
      link: '/services/kitchen-remodeling'
    },
    {
      title: 'Bathroom Remodels',
      desc: 'Spa-like retreats with expert tile work, walk-in showers, and modern fixtures',
      image: 'modern-bathroom-marble-tile-walk-in-shower-connecticut.jpg',
      alt: 'Bathroom renovation contractor Connecticut - marble tile walk-in shower modern vanity by Visser licensed insured',
      link: '/services/bathroom-remodeling'
    },
    {
      title: 'Trex Decking',
      desc: 'Low-maintenance composite deck installation for outdoor living spaces',
      image: 'composite-deck-white-railing-connecticut-home-remodeling.jpg',
      alt: 'Trex composite deck installation Connecticut - white railing outdoor living space by licensed contractor',
      link: '/services/deck-building'
    },
    {
      title: 'Finished Basements',
      desc: 'Transform unused basement space into functional living areas',
      image: 'basement-finishing-renovation-living-space-connecticut.jpg',
      alt: 'Basement finishing contractor Connecticut - professional renovation living space by Visser licensed insured',
      link: '/services/basement-finishing'
    },
  ];

  const seoData = {
    title: 'Home Remodeling Connecticut | Kitchen & Bathroom Renovations CT | Visser Construction',
    description: 'Professional home remodeling services in Connecticut. Kitchen remodels, bathroom renovations, Trex decking, basement finishing. Serving Meriden, Wallingford, Old Saybrook & CT shoreline. Free estimates.',
    keywords: 'home remodeling Connecticut, CT home renovations, kitchen remodeling CT, bathroom remodeling Connecticut, Meriden contractor, Connecticut home improvement, CT remodeling company, shoreline renovations',
    canonical: 'https://cthomeremodels.com',
    ogImage: 'https://cthomeremodels.com/og/v2/home.png',
    schemaData: generateLocalBusinessSchema()
  };

  return (
    <div className="homepage">
      <SEO {...seoData} />
      {/* Hero Section with Background Image */}
      <section
        className="relative py-32 md:py-40 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.5)), url('/api/media/name/modern-kitchen-marble-countertops-quality-remodel-connecticut-hero.jpg')`
        }}
        data-testid="hero-section"
      >
        {/* Logo in top-left corner */}
        <div className="absolute top-6 left-6 z-10">
          <img
            src="/api/media/name/visser-general-construction-llc-logo-connecticut.png"
            alt="Visser General Construction LLC Logo - Licensed and Insured Connecticut Home Remodeling Contractor"
            className="h-20 md:h-28 object-contain drop-shadow-2xl"
            data-testid="hero-logo"
          />
        </div>

        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-[#5c0701] text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              {cms('hero_badge', 'Established 2022 | Serving Connecticut')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white drop-shadow-lg">
              {cms('hero_title', 'Quality Home Remodeling in')}
              <span className="text-[#3a87fe]"> {cms('hero_title_accent', 'Connecticut')}</span>
            </h1>
            <p className="text-base md:text-lg text-white mb-8 leading-relaxed drop-shadow-md max-w-3xl mx-auto">
              {cms('hero_subtitle', 'Transform your home with expert craftsmanship from Visser General Construction. Kitchen remodels, bathroom renovations, Trex decking, basement finishing, and more across Meriden, Wallingford, Old Saybrook & CT shoreline.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-[#3a87fe] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#2968d9] transition shadow-lg"
                data-testid="hero-cta-contact"
              >
                Get a Free Quote
              </Link>
              <a
                href="tel:2036043202"
                className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-[#5c0701] transition"
                data-testid="hero-cta-call"
              >
                Call (203) 604-3202
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block bg-[#f4efe9] px-6 py-3 rounded-lg mb-6">
              <p className="text-xl md:text-2xl font-bold text-[#5c0701]">
                "{cms('mission', 'Accomplish the best quality work possible')}"
              </p>
            </div>
            <p className="text-base text-gray-600">
              {cms('mission_subtitle', 'Our mission at Visser General Construction LLC')}
            </p>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 section-gradient">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">{cms('services_title', 'Our Services')}</h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              {cms('services_subtitle', 'Comprehensive home remodeling and construction services across Connecticut')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {services.map((service, index) => (
              <Link
                to={service.link}
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                data-testid={`service-preview-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={`/api/media/name/${service.image}`}
                    alt={service.alt}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold mb-2">{service.title}</h3>
                  <p className="text-sm text-gray-600">{service.desc}</p>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/services"
              className="inline-block bg-[#3a87fe] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#2968d9] transition shadow-lg"
              data-testid="view-all-services"
            >
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-white" data-testid="testimonials-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl font-bold mb-4">{cms('testimonials_title', 'What Our Clients Say')}</h2>
              <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
                {cms('testimonials_subtitle', 'Real feedback from Connecticut homeowners who trusted us with their projects')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {testimonials.map((t, idx) => (
                <div key={idx} className="bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-lg transition-shadow duration-300" data-testid={`testimonial-card-${idx}`}>
                  <div className="flex gap-0.5 mb-4">
                    {[1, 2, 3, 4, 5].map(star => (
                      <svg key={star} className={`w-5 h-5 ${star <= t.rating ? 'text-yellow-400' : 'text-gray-300'}`} fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-4">"{t.text}"</p>
                  <div className="border-t border-gray-200 pt-4">
                    <p className="font-semibold text-sm text-gray-900">{t.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {t.city && <span className="text-xs text-gray-500">{t.city}</span>}
                      {t.city && t.service && <span className="text-xs text-gray-300">|</span>}
                      {t.service && <span className="text-xs text-[#3a87fe]">{t.service}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Service Area */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6">{cms('area_title', 'Serving Connecticut Homeowners')}</h2>
            <p className="text-base md:text-lg text-gray-600 mb-8">
              {cms('area_subtitle', 'Proudly serving homeowners within a 40-mile radius of Meriden, CT, including shoreline communities')}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                { slug: 'meriden-ct', name: 'Meriden' },
                { slug: 'wallingford-ct', name: 'Wallingford' },
                { slug: 'old-saybrook-ct', name: 'Old Saybrook' },
                { slug: 'madison-ct', name: 'Madison' },
                { slug: 'guilford-ct', name: 'Guilford' },
                { slug: 'branford-ct', name: 'Branford' },
                { slug: 'hamden-ct', name: 'Hamden' },
                { slug: 'middletown-ct', name: 'Middletown' },
                { slug: 'southington-ct', name: 'Southington' },
                { slug: 'cheshire-ct', name: 'Cheshire' },
                { slug: 'colchester-ct', name: 'Colchester' },
                { slug: 'deep-river-ct', name: 'Deep River' },
                { slug: 'clinton-ct', name: 'Clinton' },
                { slug: 'chester-ct', name: 'Chester' },
                { slug: 'westbrook-ct', name: 'Westbrook' },
                { slug: 'east-haddam-ct', name: 'East Haddam' },
                { slug: 'middlefield-ct', name: 'Middlefield' },
              ].map((city) => (
                <Link
                  key={city.slug}
                  to={`/locations/${city.slug}`}
                  className="py-2 px-3 rounded-lg text-gray-700 hover:text-[#3a87fe] hover:bg-blue-50 transition font-medium"
                  data-testid={`city-link-${city.slug}`}
                >
                  {city.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#5c0701] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">{cms('cta_title', 'Ready to Start Your Connecticut Home Project?')}</h2>
          <p className="text-base md:text-lg mb-8 max-w-2xl mx-auto">
            {cms('cta_subtitle', 'Contact us today for a free consultation and let\'s bring your vision to life')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-[#3a87fe] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#2968d9] transition shadow-lg"
              data-testid="cta-contact"
            >
              Contact Us Today
            </Link>
            <Link
              to="/portfolio"
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-[#5c0701] transition"
              data-testid="cta-portfolio"
            >
              View Our Work
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;